// Queue management for music player
// This manages a hidden playlist that persists across track changes

import { Track } from "./schema";
import { saveQueueState, clearQueueState } from "./playerState";

type QueueState = {
  tracks: Track[];
  currentTrackIndex: number;
  sourceTracks: Track[]; // Original tracks from workspace/playlist
  sourceTrackIndex: number; // Index in source tracks
};

let queueState: QueueState | null = null;
let listeners: Array<() => void> = [];

// Initialize queue with tracks from workspace/playlist
export function initializeQueue(tracks: Track[], currentTrackIndex: number) {
  queueState = {
    tracks: [...tracks], // Copy the tracks
    currentTrackIndex,
    sourceTracks: [...tracks],
    sourceTrackIndex: currentTrackIndex,
  };
  saveQueueStateToStorage();
  notifyListeners();
}

// Add track to queue after current track
export function addToQueue(track: Track): boolean {
  if (!queueState) return false;
  
  // Check if track is already in queue
  if (queueState.tracks.some(t => t.id === track.id)) {
    return false; // Track already in queue
  }
  
  // Insert track after current track
  const insertIndex = queueState.currentTrackIndex + 1;
  queueState.tracks.splice(insertIndex, 0, track);
  
  // Update current track index if needed (shouldn't be needed, but just in case)
  // Actually, we don't need to update currentTrackIndex because we're inserting after it
  
  saveQueueStateToStorage();
  notifyListeners();
  return true;
}

// Get current queue state
export function getQueue(): QueueState | null {
  return queueState;
}

// Update current track index in queue
export function setCurrentTrackIndex(index: number) {
  if (!queueState) return;
  if (index < 0 || index >= queueState.tracks.length) return;
  
  queueState.currentTrackIndex = index;
  
  // Update source track index if current track is in source tracks
  const currentTrack = queueState.tracks[index];
  const sourceIndex = queueState.sourceTracks.findIndex(t => t.id === currentTrack.id);
  if (sourceIndex >= 0) {
    queueState.sourceTrackIndex = sourceIndex;
  }
  
  saveQueueStateToStorage();
  notifyListeners();
}

// Get current track from queue
export function getCurrentTrack(): Track | null {
  if (!queueState) return null;
  if (queueState.currentTrackIndex < 0 || queueState.currentTrackIndex >= queueState.tracks.length) {
    return null;
  }
  return queueState.tracks[queueState.currentTrackIndex];
}

// Subscribe to queue changes
export function subscribe(listener: () => void) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

function notifyListeners() {
  listeners.forEach(l => l());
}

// Clear queue (when starting a new track normally)
export function clearQueue() {
  queueState = null;
  clearQueueState();
  notifyListeners();
}

// Remove track from queue if it exists
export function removeTrackFromQueue(trackId: string): void {
  if (!queueState) return;
  
  const index = queueState.tracks.findIndex(t => t.id === trackId);
  if (index < 0) return; // Track not in queue
  
  // Remove track from queue
  queueState.tracks = queueState.tracks.filter(t => t.id !== trackId);
  
  // Adjust current track index if needed
  if (index <= queueState.currentTrackIndex) {
    // If removed track was before or at current position, adjust index
    queueState.currentTrackIndex = Math.max(0, queueState.currentTrackIndex - 1);
  }
  
  // If queue is empty, clear it
  if (queueState.tracks.length === 0) {
    queueState = null;
    clearQueueState();
  } else {
    saveQueueStateToStorage();
  }
  
  notifyListeners();
}

function saveQueueStateToStorage() {
  if (!queueState) {
    clearQueueState();
    return;
  }
  
  saveQueueState({
    trackIds: queueState.tracks.map(t => t.id),
    currentTrackIndex: queueState.currentTrackIndex,
    sourceTrackIds: queueState.sourceTracks.map(t => t.id),
    sourceTrackIndex: queueState.sourceTrackIndex,
  });
}

// Restore queue from storage
export function restoreQueue(tracks: Track[], currentTrackIndex: number, sourceTracks: Track[], sourceTrackIndex: number) {
  queueState = {
    tracks: [...tracks],
    currentTrackIndex,
    sourceTracks: [...sourceTracks],
    sourceTrackIndex,
  };
  saveQueueStateToStorage();
  notifyListeners();
}

